import type { ReactNode } from "react";
import { cn } from "@ascleo/ui/lib/utils";
import {
  useStepPrimaryAction,
  useStepSecondaryAction,
  useViewTitle,
  type StepAction,
} from "../features/self-service/context";

/**
 * The three content widths a kiosk step may use. One token per step keeps the
 * content edge from jumping between neighbouring steps (FC-02, SPK width jumps):
 *
 * - `narrow` (48rem): single forms, lookups, confirmations, state messages
 * - `default` (64rem): forms with a side panel, review lists
 * - `wide` (80rem): tile grids and two-column card steps
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const width: KioskStepWidth = "narrow";
 * ```
 */
export type KioskStepWidth = "narrow" | "default" | "wide";

const KIOSK_STEP_WIDTH_CLASSES: Record<KioskStepWidth, string> = {
  narrow: "max-w-3xl",
  default: "max-w-5xl",
  wide: "max-w-7xl",
};

/**
 * Vertical placement of the step's content group. `start` aligns lead and
 * content to the top (forms, lists); `center` centres the lead and the
 * content together as one group (short state or confirmation steps), so the
 * lead never detaches from its content (KC-06, SPK-02).
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const align: KioskStepAlign = "center";
 * ```
 */
export type KioskStepAlign = "start" | "center";

/**
 * Props of {@link KioskStep}.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const props: KioskStepProps = { title: "Versicherung", children: null };
 * ```
 */
export interface KioskStepProps {
  /**
   * Translated step title, published to the shell header `h1` (the shell
   * moves focus to it and announces it on step change). Pass `null` only for
   * a step that must not show a title; every state should normally have one
   * (KO-12). A string keeps the publication stable across renders.
   */
  readonly title: string | null;
  /** Optional `data-testid` applied to the header title. */
  readonly titleTestId?: string;
  /** Optional lead paragraph, aligned to the content column (SPK-02). */
  readonly lead?: ReactNode;
  /** Content width token; defaults to `default`. */
  readonly width?: KioskStepWidth;
  /** Vertical placement of the lead + content group; defaults to `start`. */
  readonly align?: KioskStepAlign;
  /** Forward action, rendered in the fixed footer slot (see {@link useStepPrimaryAction}). */
  readonly primaryAction?: StepAction;
  /** Secondary action, rendered in the footer left of the primary action. */
  readonly secondaryAction?: StepAction;
  /** Optional `data-testid` on the step root. */
  readonly testId?: string;
  readonly children: ReactNode;
}

/**
 * Root layout for every kiosk self-service step: one padding value for all
 * flows (SPK-06), one of three named widths (FC-02), the lead inside the same
 * column as the content (SPK-02), content-sized children instead of rows that
 * stretch panels to the full height (KC-06, SPK-04), and optional footer
 * actions (KC-01). Horizontal centring against the scroll-nudge gutter is
 * owned by the shell, so steps add no side padding of their own.
 *
 * Use {@link KioskStepColumns} for a two-column body.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <KioskStep
 *   width="narrow"
 *   title={t("self-service.check-in.self-pay.title")}
 *   lead={t("self-service.check-in.self-pay.subtitle")}
 *   primaryAction={{
 *     label: t("common.next"),
 *     icon: ArrowRight,
 *     onClick: () => send({ type: "SUBMIT_SELF_PAY" }),
 *     testId: "self-pay-confirm",
 *   }}
 * >
 *   <KioskStatePanel tone="info" icon={<Wallet />} title={t("…info")} />
 * </KioskStep>
 * ```
 */
export function KioskStep({
  title,
  titleTestId,
  lead,
  width = "default",
  align = "start",
  primaryAction,
  secondaryAction,
  testId,
  children,
}: KioskStepProps) {
  useViewTitle(title, titleTestId);
  useStepPrimaryAction(primaryAction ?? null);
  useStepSecondaryAction(secondaryAction ?? null);

  return (
    <div
      data-testid={testId}
      data-kiosk-step-width={width}
      className={cn(
        "mx-auto flex min-h-full w-full min-w-0 flex-col gap-6 px-2 py-6",
        KIOSK_STEP_WIDTH_CLASSES[width],
        align === "center" && "justify-center",
      )}
    >
      {lead ? <KioskStepLead>{lead}</KioskStepLead> : null}
      {children}
    </div>
  );
}

/**
 * The step lead paragraph: 18px body text on the content column. Exposed for
 * steps that need to place the lead inside a column of {@link KioskStepColumns}.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <KioskStepLead>{t("self-service.check-in.card.subtitle")}</KioskStepLead>
 * ```
 */
export function KioskStepLead({ children }: { readonly children: ReactNode }) {
  return (
    <p className="m-0 max-w-[60ch] text-lg leading-normal text-muted-foreground">
      {children}
    </p>
  );
}

/**
 * Column ratio of {@link KioskStepColumns}: `main-wide` is 3:2, `equal` 1:1.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const ratio: KioskStepColumnRatio = "equal";
 * ```
 */
export type KioskStepColumnRatio = "main-wide" | "equal";

const KIOSK_STEP_COLUMN_CLASSES: Record<KioskStepColumnRatio, string> = {
  "main-wide": "@3xl/kiosk-columns:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]",
  equal: "@3xl/kiosk-columns:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]",
};

/**
 * Two-column step body that stays a single column until its own width
 * reaches 48rem (a container query, so the scroll gutter and readability mode
 * are accounted for). Both tracks are `minmax(0, …)` and both columns are
 * `min-w-0`, so a wide child (QR card, long KIM address) can no longer crush
 * the other column or overflow the viewport (KC-03, SPK-01). Columns are
 * top-aligned and content-sized (SPK-05).
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <KioskStepColumns aside={<KimAddressQrCode address={address} />}>
 *   <InsurerSummary insurer={insurer} />
 * </KioskStepColumns>
 * ```
 */
export function KioskStepColumns({
  aside,
  ratio = "main-wide",
  children,
}: {
  /** Secondary column; stacks below the main column on narrow widths. */
  readonly aside: ReactNode;
  /** Column ratio once split; defaults to `main-wide` (3:2). */
  readonly ratio?: KioskStepColumnRatio;
  /** Main column. */
  readonly children: ReactNode;
}) {
  return (
    <div className="@container/kiosk-columns min-w-0">
      <div
        className={cn(
          "grid min-w-0 grid-cols-1 items-start gap-6",
          KIOSK_STEP_COLUMN_CLASSES[ratio],
        )}
      >
        <div className="flex min-w-0 flex-col gap-6">{children}</div>
        <div className="flex min-w-0 flex-col gap-6">{aside}</div>
      </div>
    </div>
  );
}
